
import { Vue, Component, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { BIconArrowRepeat } from 'bootstrap-vue';
import Api from '@gid/vue-common/api';
import AgingChart from '../../components/accounting/AgingChart.vue';
import S4cAgingTrend from '../../components/accounting/S4cAgingTrend.vue';

interface AgingCohort {
  key: string;
  label: string;
}

interface AgingRow {
  accountNo: string;
  accountName: string;
  due30Plus: number;
  due0_30: number;
  d0_30: number;
  d31_60: number;
  d61_90: number;
  d90Plus: number;
  overdueTotal: number;
  openTotal: number;
}

interface SyncEntry {
  entity: string;
  last_run_at: string | null;
  ok: boolean;
  error: string | null;
}

const AMOUNT_KEYS = [
  'due30Plus',
  'due0_30',
  'd0_30',
  'd31_60',
  'd61_90',
  'd90Plus',
  'overdueTotal',
  'openTotal',
] as const;

// A nightly sync that has not run for more than this is stale enough that the
// numbers should not be read as current.
const STALE_AFTER_HOURS = 36;

const eur = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

/**
 * AR/AP aging for Service4Charger, read from the nightly Business Central mirror.
 *
 * Deliberately the same shape as the GID aging at `/accounting/aging` — same
 * cohorts, same chart, same vocabulary — so the two can be read side by side.
 * What must NOT be done is add them together: the parties here are BC customers
 * and vendors, which have no mapping to GID accounts, and the two systems bill
 * for different things.
 */
@Component({
  components: { AgingChart, S4cAgingTrend, BIconArrowRepeat },
})
export default class S4cAging extends Vue {
  kind: 'invoices' | 'costs' = 'invoices';
  topN: number | 'all' = 20;
  historyDays = 182;
  search = '';

  inProgress = false;
  errorMessage = '';

  allRows: AgingRow[] = [];
  cohorts: AgingCohort[] = [];
  totalsFromApi: Record<string, number> = {};
  sync: SyncEntry[] = [];
  undated = 0;
  trendPoints: any[] = [];

  selectedAccountNo = '';
  documentsOpen = false;
  documentsLoading = false;
  documentsError = '';
  documents: any[] = [];
  documentsAccountName = '';

  created() {
    this.fetch();
  }

  @Watch('historyDays')
  onHistoryDaysChange() {
    this.fetchHistory();
  }

  get kindOptions() {
    return [
      { value: 'invoices', text: this.$t('accounting.s4c-aging.invoices') },
      { value: 'costs', text: this.$t('accounting.s4c-aging.costs') },
    ];
  }

  get topNOptions() {
    return [
      { value: 10, text: 'Top 10' },
      { value: 20, text: 'Top 20' },
      { value: 50, text: 'Top 50' },
      { value: 'all', text: this.$t('accounting.s4c-aging.all') },
    ];
  }

  get historyOptions() {
    return [
      { value: 30, text: this.$t('accounting.s4c-aging.days', { n: 30 }) },
      { value: 90, text: this.$t('accounting.s4c-aging.days', { n: 90 }) },
      { value: 182, text: this.$t('accounting.s4c-aging.days', { n: 182 }) },
      { value: 365, text: this.$t('accounting.s4c-aging.days', { n: 365 }) },
    ];
  }

  get filteredRows(): AgingRow[] {
    const needle = this.search.trim().toLowerCase();
    if (!needle) {
      return this.allRows;
    }
    return this.allRows.filter(
      (r) =>
        (r.accountName || '').toLowerCase().includes(needle) ||
        (r.accountNo || '').toLowerCase().includes(needle),
    );
  }

  get displayRows(): AgingRow[] {
    return this.topN === 'all'
      ? this.filteredRows
      : this.filteredRows.slice(0, this.topN as number);
  }

  /**
   * Totals follow the *filtered* set, not the API's grand total, so the footer
   * always adds up to what is on screen. The unfiltered total is still available
   * from the API payload and is what the KPI tiles show.
   */
  get totals(): Record<string, number> {
    const acc: Record<string, number> = {};
    for (const k of AMOUNT_KEYS) {
      acc[k] = Number(
        this.filteredRows.reduce((s, r) => s + (r[k] || 0), 0).toFixed(2),
      );
    }
    return acc;
  }

  get kpis() {
    const t = this.totalsFromApi;
    const open = t.openTotal || 0;
    const overdue = t.overdueTotal || 0;
    return [
      {
        key: 'open',
        label: this.$t('accounting.s4c-aging.open-total'),
        value: this.formatEur(open),
        cls: '',
      },
      {
        key: 'overdue',
        label: this.$t('accounting.s4c-aging.overdue-total'),
        value: this.formatEur(overdue),
        cls: overdue > 0 ? 'text-danger' : '',
      },
      {
        key: 'share',
        label: this.$t('accounting.s4c-aging.overdue-share'),
        value: open ? `${Math.round((overdue / open) * 100)} %` : '–',
        cls: '',
      },
      {
        key: 'parties',
        label: this.$t('accounting.s4c-aging.parties'),
        value: String(this.allRows.length),
        cls: '',
      },
    ];
  }

  get lastRunAt(): string | null {
    const stamps = this.sync
      .map((s) => s.last_run_at)
      .filter((s): s is string => !!s);
    return stamps.length ? stamps.sort()[stamps.length - 1] : null;
  }

  get syncLabel(): string {
    return this.lastRunAt
      ? (this.$t('accounting.s4c-aging.synced', {
          when: moment(this.lastRunAt).format('DD.MM.YYYY HH:mm'),
        }) as string)
      : '';
  }

  get syncStale(): boolean {
    if (!this.sync.length) {
      return false;
    }
    if (this.sync.some((s) => !s.ok)) {
      return true;
    }
    return this.lastRunAt
      ? moment().diff(moment(this.lastRunAt), 'hours') > STALE_AFTER_HOURS
      : true;
  }

  get chartHeight(): number {
    // ~28px per bar + headroom for legend/axes
    return Math.max(360, this.displayRows.length * 28 + 120);
  }

  // Cohort labels come from the backend in English; translate them here by key
  // so the chart legend and table headers follow the UI locale. The keys are
  // shared with the GID aging, so its translations are reused.
  get localizedCohorts(): AgingCohort[] {
    return this.cohorts.map((c) => ({
      key: c.key,
      label: this.$te(`accounting.aging.cohort.${c.key}`)
        ? (this.$t(`accounting.aging.cohort.${c.key}`) as string)
        : c.label,
    }));
  }

  get fields() {
    return [
      {
        key: 'accountName',
        label: this.$t('accounting.s4c-aging.account'),
        sortable: true,
      },
      ...this.localizedCohorts.map((c) => ({
        key: c.key,
        label: c.label,
        sortable: true,
        class: 'text-right',
      })),
      {
        key: 'overdueTotal',
        label: this.$t('accounting.s4c-aging.overdue-total'),
        sortable: true,
        class: 'text-right',
      },
      {
        key: 'openTotal',
        label: this.$t('accounting.s4c-aging.open-total'),
        sortable: true,
        class: 'text-right',
      },
    ];
  }

  get documentFields() {
    return [
      {
        key: 'documentType',
        label: this.$t('accounting.s4c-aging.document-type'),
        sortable: true,
      },
      {
        key: 'documentNo',
        label: this.$t('accounting.s4c-aging.document-no'),
        sortable: true,
      },
      {
        key: 'postingDate',
        label: this.$t('accounting.s4c-aging.posting-date'),
        sortable: true,
      },
      {
        key: 'dueDate',
        label: this.$t('accounting.s4c-aging.due-date'),
        sortable: true,
      },
      {
        key: 'daysOverdue',
        label: this.$t('accounting.s4c-aging.days-overdue'),
        sortable: true,
        class: 'text-right',
      },
      {
        key: 'originalAmount',
        label: this.$t('accounting.s4c-aging.original-amount'),
        sortable: true,
        class: 'text-right',
      },
      {
        key: 'remainingAmount',
        label: this.$t('accounting.s4c-aging.remaining-amount'),
        sortable: true,
        class: 'text-right',
      },
    ];
  }

  get documentsTitle(): string {
    return this.$t('accounting.s4c-aging.documents-title', {
      account: this.documentsAccountName || this.selectedAccountNo,
    }) as string;
  }

  async fetch() {
    this.inProgress = true;
    this.errorMessage = '';
    try {
      const { data } = await Api.get('/api/admin/s4c/accounting/aging', {
        params: { kind: this.kind },
        autoCancelScope: 's4c-aging',
      });
      this.allRows = data.rows || [];
      this.cohorts = data.cohorts || [];
      this.totalsFromApi = data.totals || {};
      this.sync = data.sync || [];
      this.undated = data.undated || 0;
    } catch (e: any) {
      this.errorMessage = e?.response?.data?.message || e?.message || String(e);
      this.allRows = [];
      this.totalsFromApi = {};
    } finally {
      this.inProgress = false;
    }
    await this.fetchHistory();
  }

  async fetchHistory() {
    try {
      const { data } = await Api.get(
        '/api/admin/s4c/accounting/aging/history',
        {
          params: { kind: this.kind, days: this.historyDays },
          autoCancelScope: 's4c-aging-history',
        },
      );
      this.trendPoints = data.points || [];
    } catch (e) {
      // The trend is supplementary: a missing snapshot series must not take the
      // whole report down, and the error is already visible as an empty chart.
      this.trendPoints = [];
    }
  }

  async onSelectAccount(row: AgingRow) {
    this.selectedAccountNo = row.accountNo;
    this.documentsAccountName = row.accountName;
    this.documents = [];
    this.documentsError = '';
    this.documentsOpen = true;
    this.documentsLoading = true;
    try {
      const { data } = await Api.get(
        '/api/admin/s4c/accounting/aging/documents',
        {
          params: { kind: this.kind, account_no: row.accountNo },
          autoCancelScope: 's4c-aging-documents',
        },
      );
      this.documents = data.documents || [];
    } catch (e: any) {
      this.documentsError =
        e?.response?.data?.message || e?.message || String(e);
    } finally {
      this.documentsLoading = false;
    }
  }

  onDocumentsHidden() {
    this.documents = [];
    this.documentsError = '';
  }

  rowClass(item: AgingRow) {
    return item && item.accountNo === this.selectedAccountNo
      ? 'table-primary'
      : '';
  }

  amountClass(value: number) {
    if (value < 0) return 'text-danger';
    return '';
  }

  formatEur(value: number): string {
    return eur.format(Number(value) || 0);
  }
}
